import type { SubmitHandler } from 'react-hook-form';

import type { IContainerProps } from '~/components/utils/Container';
import type { IApiUpdateAppSettingsResponseBody } from '~/lib/api/modules/AppSettings';
import type { IUseUpdateAppSettingsMutationValues } from '~/lib/api/modules/AppSettings/hooks/updateAppSettingsMutation';
import type { IFormatsSettingsFormValues } from './FormatsSettingsForm';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { useAppQuery } from '~/lib/api/modules/Apps/hooks/useAppQuery';
import { useUpdateAppSettingsMutation } from '~/lib/api/modules/AppSettings/hooks/updateAppSettingsMutation';
import { FormatsSettingsForm } from './FormatsSettingsForm';

export type IUpdateFormatsSettingsFormProps = IContainerProps & {
  onSuccess?: (
    outputData: IApiUpdateAppSettingsResponseBody,
    inputValues: IUseUpdateAppSettingsMutationValues,
  ) => void;
  cancelUrl?: string;
  appId: string;
};

export const UpdateFormatsSettingsForm: React.FC<
  IUpdateFormatsSettingsFormProps
> = (props) => {
  const { sx, onSuccess, cancelUrl, appId } = props;
  const updateAppSettingsMutation = useUpdateAppSettingsMutation();
  const appQuery = useAppQuery({ appId });
  const appSettings = appQuery.data?.settings;

  const handleSubmit: SubmitHandler<IFormatsSettingsFormValues> = (values) => {
    if (updateAppSettingsMutation.isLoading || !appSettings) {
      return;
    }

    return updateAppSettingsMutation.mutateAsync(
      {
        appId,
        body: {
          ...appSettings,
          press: {
            ...appSettings.press,
            env: {
              ...appSettings.press?.env,
              articleFromCommunityDateFormat: values.articleDateFormat,
              articleFromFeedDateFormat: values.articleDateFormat,
              categoryArticleDateFormat: values.categoryDateFormat,
              communityArticleDateFormat: values.categoryDateFormat,
              feedArticleDateFormat: values.feedDateFormat,
            },
          },
        },
      },
      { onSuccess },
    );
  };

  return (
    <ContentBlock sx={sx}>
      <ContentBlock.Suspense
        loading={appQuery.isLoading}
        error={appQuery.error}
        first
      >
        {appSettings ? (
          <FormatsSettingsForm
            first
            mode="edition"
            defaultValues={{
              articleDateFormat:
                appSettings.press?.env?.articleFromFeedDateFormat,
              categoryDateFormat:
                appSettings.press?.env?.categoryArticleDateFormat,
              feedDateFormat: appSettings.press?.env?.feedArticleDateFormat,
            }}
            cancelUrl={cancelUrl}
            onSubmit={handleSubmit}
          />
        ) : null}
      </ContentBlock.Suspense>
    </ContentBlock>
  );
};
